// src/contexts/AgencyContext.tsx
// BRIDGE FIX: maps frontend's expected 'agency_users' / 'agencies' pattern
// to real DB tables: 'tenant_users' / 'tenants' in visaguide schema
// Interface is IDENTICAL to original — zero page changes needed

import React, {
  createContext, useContext, useState,
  useEffect, useCallback,
} from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';

// ─── Types (unchanged from original) ─────────────────────────────────────────

interface Agency {
  id: string;
  name: string;
  slug: string;
  created_by: string;
  created_at: string;
  // Extended fields from tenants table
  plan?: string;
  status?: string;
  settings?: Record<string, any>;
}

interface AgencyUser {
  id: string;
  agency_id: string;
  user_id: string;
  role: 'admin' | 'member' | 'viewer' | 'owner';
  accepted_at: string | null;
  users?: {
    id: string;
    email: string;
    full_name: string;
  };
}

interface AgencyContextType {
  currentAgency: Agency | null;
  userRole: string | null;
  agencies: Agency[];
  loading: boolean;
  members: AgencyUser[];
  rlsError: string | null;
  refreshAgencies: () => Promise<void>;
  createAgency: (name: string) => Promise<Agency | null>;
  switchAgency: (agencyId: string) => Promise<void>;
  inviteMember: (email: string, role: string) => Promise<boolean>;
  removeMember: (userId: string) => Promise<boolean>;
  updateMemberRole: (userId: string, role: string) => Promise<boolean>;
}

const AgencyContext = createContext<AgencyContextType | undefined>(undefined);

// Real Postgres enum values (visaguide."UserRole"), confirmed via schema
// introspection -- NOT 'OWNER'/'ADMIN'. The three separate inline copies of
// this map that used to live in this file (mapTenantUserToAgencyUser,
// loadAgencies, switchAgency) all keyed on the wrong strings, so a real
// TENANT_OWNER/TENANT_ADMIN row silently fell through to the 'viewer'
// fallback -- including the current user's own role, which gates
// removeMember/updateMemberRole on `userRole === 'admin'`. Consolidated to
// one map so this can't drift out of sync between call sites again.
const DB_ROLE_TO_APP_ROLE: Record<string, string> = {
  TENANT_OWNER: 'admin',
  TENANT_ADMIN: 'admin',
  SUPER_ADMIN: 'admin',
  CONSULTANT: 'member',
  PARALEGAL: 'member',
  CASE_MANAGER: 'member',
  VIEWER: 'viewer',
  CLIENT: 'viewer',
};

// ─── Helper: map tenant → agency shape ───────────────────────────────────────
// Converts Prisma camelCase tenant to snake_case Agency the frontend expects
function mapTenantToAgency(tenant: any): Agency {
  return {
    id: tenant.id,
    name: tenant.name,
    slug: tenant.slug ?? '',
    created_by: tenant.createdBy ?? tenant.created_by ?? '',
    created_at: tenant.createdAt ?? tenant.created_at ?? new Date().toISOString(),
    plan: tenant.plan,
    status: tenant.status,
    settings: tenant.settings ?? {},
  };
}

// ─── Helper: map tenant_user → agency_user shape ──────────────────────────────
function mapTenantUserToAgencyUser(tu: any, tenantId: string): AgencyUser {
  return {
    id: tu.id,
    agency_id: tenantId,
    user_id: tu.userId ?? tu.user_id,
    role: (DB_ROLE_TO_APP_ROLE[tu.role] ?? 'viewer') as any,
    accepted_at: tu.joinedAt ?? tu.joined_at ?? null,
  };
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function AgencyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const [currentAgency, setCurrentAgency] = useState<Agency | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [members, setMembers] = useState<AgencyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [rlsError, setRlsError] = useState<string | null>(null);

  const loadAgencies = useCallback(async () => {
    if (!user?.id) {
      setAgencies([]); setCurrentAgency(null);
      setMembers([]); setUserRole(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setRlsError(null);

    try {
      // ── Step 1: get tenant memberships for this user ─────────────────────
      // DB table: visaguide.tenant_users, field: userId (camelCase)
      const { data: memberships, error: membershipError } = await supabase
        
        .from('agency_members')
        .select('id, tenantId, userId, role, joinedAt')
        .eq('userId', user.id);

      if (membershipError) {
        console.error('🚨 Agency membership error:', membershipError);
        setRlsError('Agency system temporarily unavailable.');
        setLoading(false);
        return;
      }

      const tenantIds = (memberships ?? []).map((m: any) => m.tenantId).filter(Boolean);

      if (!tenantIds.length) {
        setAgencies([]); setCurrentAgency(null);
        setUserRole(null); setLoading(false);
        return;
      }

      // ── Step 2: fetch tenants ────────────────────────────────────────────
      const { data: tenantsData, error: tenantsError } = await supabase
        
        .from('agency_tenants')
        .select('id, name, slug, plan, status, settings, createdAt')
        .in('id', tenantIds);

      if (tenantsError) {
        console.error('🚨 Tenants fetch error:', tenantsError);
        setLoading(false);
        return;
      }

      const agencyList = (tenantsData ?? []).map(mapTenantToAgency);
      setAgencies(agencyList);

      // ── Step 3: restore selected agency ─────────────────────────────────
      const storedId = localStorage.getItem('current_agency_id');
      const selected = agencyList.find(a => a.id === storedId) ?? agencyList[0] ?? null;
      setCurrentAgency(selected);

      // ── Step 4: set role ─────────────────────────────────────────────────
      const myMembership = (memberships ?? []).find((m: any) => m.tenantId === selected?.id);
      setUserRole(myMembership ? (DB_ROLE_TO_APP_ROLE[myMembership.role] ?? 'viewer') : null);

      // ── Step 5: load members for selected agency ─────────────────────────
      if (selected) {
        const { data: memberData } = await supabase
          
          .from('agency_members')
          .select('id, tenantId, userId, role, joinedAt')
          .eq('tenantId', selected.id);

        setMembers((memberData ?? []).map((m: any) => mapTenantUserToAgencyUser(m, selected.id)));
      }

    } catch (err: any) {
      console.error('🚨 AgencyContext crash:', err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadAgencies(); }, [loadAgencies]);

  // ── Create agency → creates tenant ───────────────────────────────────────
  const createAgency = async (name: string): Promise<Agency | null> => {
    if (!user?.id) return null;
    try {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now();
      const tenantId = crypto.randomUUID();
      const now = new Date().toISOString();

      const { error: tenantError } = await supabase
        
        .from('agency_tenants')
        .insert({
          id: tenantId, name, slug,
          type: 'AGENCY', status: 'ACTIVE', plan: 'starter',
          maxUsers: 10, maxCases: 100,
          settings: '{}', metadata: '{}',
          createdAt: now, updatedAt: now,
        });

      if (tenantError) { console.error('Create tenant error:', tenantError); return null; }

      await supabase.from('agency_members').insert({
        id: crypto.randomUUID(),
        tenantId, userId: user.id,
        // Real enum is visaguide."UserRole" -- 'OWNER' is not a valid value
        // and this insert would fail (22P02) exactly like it did while
        // building the agency_members RLS migration tonight.
        role: 'TENANT_OWNER', permissions: '["*"]',
        joinedAt: now, updatedAt: now,
      });

      // Ensure user profile exists
      await supabase.from('agency_users').upsert({
        id: user.id, email: user.email,
        fullName: user.email?.split('@')[0] ?? 'Agency Owner',
        status: 'ACTIVE', createdAt: now, updatedAt: now,
      }, { onConflict: 'id' });

      await loadAgencies();

      return { id: tenantId, name, slug, created_by: user.id, created_at: now };
    } catch (err: any) {
      console.error('Create agency crash:', err.message);
      return null;
    }
  };

  // ── Switch agency ────────────────────────────────────────────────────────
  const switchAgency = async (agencyId: string) => {
    const agency = agencies.find(a => a.id === agencyId) ?? null;
    if (!agency) return;
    setCurrentAgency(agency);
    localStorage.setItem('current_agency_id', agencyId);

    const { data: memberData } = await supabase
      
      .from('agency_members')
      .select('id, tenantId, userId, role, joinedAt')
      .eq('tenantId', agencyId);
    setMembers((memberData ?? []).map((m: any) => mapTenantUserToAgencyUser(m, agencyId)));

    const myRecord = (memberData ?? []).find((m: any) => m.userId === user?.id);
    if (myRecord) {
      setUserRole(DB_ROLE_TO_APP_ROLE[myRecord.role] ?? 'viewer');
    }
  };

  // ── Invite member (creates pending record) ────────────────────────────────
  const inviteMember = async (email: string, role: string): Promise<boolean> => {
    if (!currentAgency) return false;
    try {
      const { error } = await supabase
        
        .from('invitations')
        .insert({
          id: crypto.randomUUID(),
          tenantId: currentAgency.id,
          email,
          role: role.toUpperCase(),
          token: Math.random().toString(36).substring(2),
          expiresAt: new Date(Date.now() + 7 * 86400000).toISOString(),
          createdAt: new Date().toISOString(),
        });
      return !error;
    } catch { return false; }
  };

  // ── Remove member ─────────────────────────────────────────────────────────
  const removeMember = async (userId: string): Promise<boolean> => {
    if (!currentAgency || userRole !== 'admin') return false;
    const { error } = await supabase
      
      .from('agency_members')
      .delete()
      .eq('tenantId', currentAgency.id)
      .eq('userId', userId);
    if (!error) await loadAgencies();
    return !error;
  };

  // ── Update member role ────────────────────────────────────────────────────
  const updateMemberRole = async (userId: string, role: string): Promise<boolean> => {
    if (!currentAgency || userRole !== 'admin') return false;
    const dbRole = role.toUpperCase() === 'ADMIN' ? 'TENANT_ADMIN'
      : role.toUpperCase() === 'MEMBER' ? 'CONSULTANT'
      : 'VIEWER';
    const { error } = await supabase
      
      .from('agency_members')
      .update({ role: dbRole, updatedAt: new Date().toISOString() })
      .eq('tenantId', currentAgency.id)
      .eq('userId', userId);
    if (!error) await loadAgencies();
    return !error;
  };

  return (
    <AgencyContext.Provider value={{
      currentAgency, userRole, agencies, loading, members, rlsError,
      refreshAgencies: loadAgencies,
      createAgency, switchAgency, inviteMember, removeMember, updateMemberRole,
    }}>
      {children}
    </AgencyContext.Provider>
  );
}

export const useAgency = () => {
  const ctx = useContext(AgencyContext);
  if (!ctx) throw new Error('useAgency must be used within AgencyProvider');
  return ctx;
};
